import { getApiErrorMessage } from "@utils/error-resolvers";
import { APIErrorCodes } from "kz-ui-sdk";

export const PROMOTION_ALREADY_CLAIMED_GUARD_CODES = [
  APIErrorCodes.ClaimReward.AlreadyClaimed,
  APIErrorCodes.ClaimReward.UnableRedeem,
] as const;
const CLAIM_GUARD_MESSAGE_FIELDS = ["message", "msg", "code"] as const;
const CLAIM_GUARD_NESTED_FIELDS = ["error", "data"] as const;

function normalizeClaimGuardCode(code: string): string {
  return code
    .trim()
    .toLowerCase()
    .replace(/[.!]+$/g, "");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

function getPromotionClaimGuardMessages(resultOrError: unknown, seen = new WeakSet<object>()): string[] {
  if (typeof resultOrError === "string") return [resultOrError];
  if (!isRecord(resultOrError)) return [];

  if (seen.has(resultOrError)) return [];
  seen.add(resultOrError);

  const messages: string[] = [];

  const apiErrorMessage = getApiErrorMessage(resultOrError);
  if (apiErrorMessage) messages.push(apiErrorMessage);

  CLAIM_GUARD_MESSAGE_FIELDS.forEach((field) => {
    const message = resultOrError[field];
    if (typeof message === "string") messages.push(message);
  });

  CLAIM_GUARD_NESTED_FIELDS.forEach((field) => {
    messages.push(...getPromotionClaimGuardMessages(resultOrError[field], seen));
  });

  const errors = resultOrError.errors;
  const nestedErrors = Array.isArray(errors) ? errors : isRecord(errors) ? Object.values(errors) : [];
  nestedErrors.forEach((error) => {
    if (typeof error === "string") {
      messages.push(error);
      return;
    }

    if (isRecord(error)) {
      CLAIM_GUARD_MESSAGE_FIELDS.forEach((field) => {
        const message = error[field];
        if (typeof message === "string") messages.push(message);
      });
      messages.push(...getPromotionClaimGuardMessages(error, seen));
    }
  });

  return Array.from(new Set(messages));
}

export function matchesClaimGuardCode(resultOrError: unknown, codes: readonly string[]): boolean {
  const normalizedCodes = codes.map(normalizeClaimGuardCode);

  return getPromotionClaimGuardMessages(resultOrError).some((message) =>
    normalizedCodes.includes(normalizeClaimGuardCode(message)),
  );
}

export function isAlreadyClaimedGuardError(resultOrError: unknown, extraCodes: readonly string[] = []): boolean {
  const messages = getPromotionClaimGuardMessages(resultOrError);
  if (!messages.length) return false;

  const claimGuardCodes = [...PROMOTION_ALREADY_CLAIMED_GUARD_CODES, ...extraCodes];
  const normalizedClaimGuardCodes = claimGuardCodes.map(normalizeClaimGuardCode);

  return messages.some((message) => {
    const normalizedMessage = normalizeClaimGuardCode(message);

    return normalizedClaimGuardCodes.some((code) => {
      return (
        normalizedMessage === code ||
        (code === normalizeClaimGuardCode(APIErrorCodes.ClaimReward.AlreadyClaimed) && normalizedMessage.includes(code))
      );
    });
  });
}
